import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  TOAST_VARIANTS,
  notificationSubline,
  type AppointmentNotification,
} from '../hooks/useAppointmentNotifications';

const TOAST_DURATION_MS = 5_000;
const TOAST_RESUME_MS = 2_500;
const MAX_VISIBLE = 3;

export function ToastIcon({ type, size = 36 }: { type: AppointmentNotification['type']; size?: number }) {
  return (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0 text-white"
      style={{ width: size, height: size, background: TOAST_VARIANTS[type].bg }}
    >
      {type === 'booked' ? (
        <svg width={size * 0.47} height={size * 0.47} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="5 12.5 10 17.5 19 7.5" />
        </svg>
      ) : (
        <svg width={size * 0.42} height={size * 0.42} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 6l12 12" /><path d="M18 6L6 18" />
        </svg>
      )}
    </div>
  );
}

function BookingToast({
  item,
  onClose,
  onClick,
}: {
  item: AppointmentNotification;
  onClose: (id: string) => void;
  onClick: (id: string) => void;
}) {
  const [paused, setPaused] = useState(false);
  const [remaining, setRemaining] = useState(TOAST_DURATION_MS);

  useEffect(() => {
    if (paused) return;
    const timer = window.setTimeout(() => onClose(item.id), remaining);
    return () => window.clearTimeout(timer);
  }, [paused, remaining, item.id, onClose]);

  return (
    <motion.button
      layout="position"
      type="button"
      initial={{ opacity: 0, x: 48, scale: 0.94 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 48, scale: 0.96, transition: { duration: 0.2 } }}
      transition={{ type: 'spring', stiffness: 420, damping: 32 }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => {
        setRemaining(TOAST_RESUME_MS);
        setPaused(false);
      }}
      onClick={() => onClick(item.id)}
      className="pointer-events-auto flex items-center gap-2 pl-2 pr-3 py-2 text-left w-max max-w-[min(320px,calc(100vw-32px))]"
      style={{
        background: '#120E1F',
        borderRadius: 12,
        boxShadow: '0 8px 18px rgba(14,11,30,0.22), 0 1px 4px rgba(14,11,30,0.12)',
      }}
      aria-label={`${TOAST_VARIANTS[item.type].title}: ${notificationSubline(item)} – schließen`}
    >
      <ToastIcon type={item.type} size={24} />
      <div className="min-w-0">
        <div
          className="text-[13px] font-bold text-white leading-tight"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          {TOAST_VARIANTS[item.type].title}
        </div>
        <div
          className="text-[12px] font-medium leading-tight mt-px truncate"
          style={{ color: 'rgba(255,255,255,0.62)' }}
        >
          {notificationSubline(item)}
        </div>
      </div>
    </motion.button>
  );
}

/**
 * Zeigt neue Buchungs-/Storno-Meldungen kurz als Floating-Toast oben rechts,
 * direkt unter der Benachrichtigungs-Glocke im Header (kommt von rechts hereingeflogen).
 * Der erste erfolgreiche Abruf dient nur als Ausgangsbasis, damit beim Öffnen
 * des Dashboards keine alten Meldungen aufpoppen.
 */
export default function BookingToastHost({
  notifications,
  fetchedAt,
  onRead,
}: {
  notifications: AppointmentNotification[];
  fetchedAt: number | null;
  onRead: (ids: string[]) => void;
}) {
  const seenRef = useRef<Set<string> | null>(null);
  const [queue, setQueue] = useState<AppointmentNotification[]>([]);

  useEffect(() => {
    if (fetchedAt === null) return;
    if (!seenRef.current) {
      seenRef.current = new Set(notifications.map(n => n.id));
      return;
    }
    const seen = seenRef.current;
    // API liefert neueste zuerst; Toasts in zeitlicher Reihenfolge anstellen
    const fresh = notifications.filter(n => !seen.has(n.id)).reverse();
    if (!fresh.length) return;
    fresh.forEach(n => seen.add(n.id));
    setQueue(prev => [...prev, ...fresh]);
  }, [notifications, fetchedAt]);

  const close = useCallback((id: string) => {
    setQueue(prev => prev.filter(n => n.id !== id));
  }, []);

  // Klick schließt den Toast und markiert die Meldung in der Inbox als gelesen
  const handleClick = (id: string) => {
    close(id);
    onRead([id]);
  };

  return (
    <div
      className="fixed top-[64px] right-4 lg:right-8 z-[60] flex flex-col items-end gap-2.5 pointer-events-none"
      role="status"
      aria-live="polite"
    >
      <AnimatePresence initial={false}>
        {queue.slice(0, MAX_VISIBLE).map(item => (
          <BookingToast key={item.id} item={item} onClose={close} onClick={handleClick} />
        ))}
      </AnimatePresence>
    </div>
  );
}
